import React, { useState, useEffect } from 'react';
import { Shield, Check, X, RefreshCw, AlertCircle, Loader2, UserCog, Users, Search } from 'lucide-react';
import { useUserManagement } from '../hooks';

export function AdminView() {
  const {
    pendingUsers,
    allUsers,
    loading,
    error,
    approveUser,
    rejectUser,
    promoteToAdmin,
    refreshUsers,
    refreshAllUsers
  } = useUserManagement();
  const [processingUserId, setProcessingUserId] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Fetch all users when "All Users" tab is activated
  useEffect(() => {
    if (activeTab === 'all') {
      refreshAllUsers();
    }
  }, [activeTab, refreshAllUsers]);

  const handleApprove = async (userId) => {
    setProcessingUserId(userId);
    const result = await approveUser(userId);
    if (result.success) {
      // Success feedback handled by list refresh
    }
    setProcessingUserId(null);
  };

  const handleReject = async (userId) => {
    setProcessingUserId(userId);
    const result = await rejectUser(userId);
    if (result.success) {
      // Success feedback handled by list refresh
    }
    setProcessingUserId(null);
  };

  const handlePromoteToAdmin = async (userId) => {
    if (!window.confirm('Are you sure you want to promote this user to admin? This will grant them full administrative privileges.')) {
      return;
    }

    setProcessingUserId(userId);
    const result = await promoteToAdmin(userId);
    if (result.success) {
      alert('User promoted successfully! They must sign out and sign back in to see admin features.');
    } else {
      alert(`Failed to promote user: ${result.error || 'Unknown error'}`);
    }
    setProcessingUserId(null);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp).toLocaleString();
  };

  // Filter and search users
  const getFilteredUsers = () => {
    let filtered = allUsers;

    // Filter by status
    if (statusFilter !== 'all') {
      if (statusFilter === 'admin') {
        filtered = filtered.filter(u => u.isAdmin);
      } else {
        filtered = filtered.filter(u => u.status === statusFilter);
      }
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(u =>
        u.displayName?.toLowerCase().includes(query) ||
        u.email?.toLowerCase().includes(query)
      );
    }

    return filtered;
  };

  // Check if user can be promoted
  const canPromoteToAdmin = (user) => {
    return user.status === 'approved' && !user.isAdmin;
  };

  // Get status badge styling
  const getStatusBadge = (user) => {
    if (user.isAdmin) {
      return { text: 'Admin', class: 'bg-purple-100 text-purple-700' };
    }
    switch (user.status) {
      case 'pending':
        return { text: 'Pending', class: 'bg-yellow-100 text-yellow-700' };
      case 'approved':
        return { text: 'Approved', class: 'bg-green-100 text-green-700' };
      case 'rejected':
        return { text: 'Rejected', class: 'bg-red-100 text-red-700' };
      default:
        return { text: 'Unknown', class: 'bg-gray-100 text-gray-700' };
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Shield className="text-blue-600" size={24} />
          <h2 className="text-lg font-semibold text-gray-900">Admin Panel</h2>
        </div>
        <button
          onClick={() => activeTab === 'pending' ? refreshUsers() : refreshAllUsers()}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors font-medium"
        >
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg mb-4">
          <AlertCircle size={18} />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('pending')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'pending'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <AlertCircle size={18} />
          Pending Requests
          {pendingUsers.length > 0 && (
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              activeTab === 'pending'
                ? 'bg-blue-500 text-white'
                : 'bg-yellow-100 text-yellow-700'
            }`}>
              {pendingUsers.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('all')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'all'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <Users size={18} />
          All Users
          {allUsers.length > 0 && (
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              activeTab === 'all'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-500 text-white'
            }`}>
              {allUsers.length}
            </span>
          )}
        </button>
      </div>

      {/* Search and Filter Bar (for All Users tab) */}
      {activeTab === 'all' && (
        <div className="mb-4 space-y-3">
          {/* Search bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Status filter pills */}
          <div className="flex gap-2 flex-wrap">
            {['all', 'pending', 'approved', 'rejected', 'admin'].map(status => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  statusFilter === status
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Content Area - Pending Users Tab */}
      {activeTab === 'pending' && (
        <div className="bg-white border border-gray-200 rounded-xl">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">
              Pending Access Requests
              {pendingUsers.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-700 text-sm rounded-full">
                  {pendingUsers.length}
                </span>
              )}
            </h3>
          </div>

          {loading && pendingUsers.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-gray-400" size={32} />
            </div>
          ) : pendingUsers.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              No pending access requests
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {pendingUsers.map((user) => (
                <div key={user.userId} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {user.photoURL ? (
                        <img
                          src={user.photoURL}
                          alt={user.displayName}
                          className="w-12 h-12 rounded-full"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                          <span className="text-gray-600 font-medium text-lg">
                            {user.displayName?.charAt(0) || '?'}
                          </span>
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-gray-900">
                          {user.displayName || 'Unknown User'}
                        </div>
                        <div className="text-sm text-gray-600">{user.email}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          Requested: {formatDate(user.requestedAt)}
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleApprove(user.userId)}
                        disabled={processingUserId === user.userId || loading}
                        className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
                      >
                        {processingUserId === user.userId ? (
                          <Loader2 className="animate-spin" size={16} />
                        ) : (
                          <Check size={16} />
                        )}
                        Approve
                      </button>
                      <button
                        onClick={() => handleReject(user.userId)}
                        disabled={processingUserId === user.userId || loading}
                        className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
                      >
                        {processingUserId === user.userId ? (
                          <Loader2 className="animate-spin" size={16} />
                        ) : (
                          <X size={16} />
                        )}
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Content Area - All Users Tab */}
      {activeTab === 'all' && (
        <div className="bg-white border border-gray-200 rounded-xl">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">
              {getFilteredUsers().length} Users
              {searchQuery && ` matching "${searchQuery}"`}
            </h3>
          </div>

          {loading && allUsers.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-gray-400" size={32} />
            </div>
          ) : getFilteredUsers().length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              {searchQuery ? 'No users found matching your search' : 'No users found'}
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {getFilteredUsers().map((user) => {
                const badge = getStatusBadge(user);

                return (
                  <div key={user.userId} className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        {user.photoURL ? (
                          <img
                            src={user.photoURL}
                            alt={user.displayName}
                            className="w-12 h-12 rounded-full"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                            <span className="text-gray-600 font-medium text-lg">
                              {user.displayName?.charAt(0) || '?'}
                            </span>
                          </div>
                        )}

                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <div className="font-medium text-gray-900">
                              {user.displayName || 'Unknown User'}
                            </div>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.class}`}>
                              {badge.text}
                            </span>
                          </div>

                          <div className="text-sm text-gray-600">{user.email}</div>

                          {/* Metadata */}
                          <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                            <div>Requested: {formatDate(user.requestedAt)}</div>
                            {user.approvedAt && (
                              <div>Approved: {formatDate(user.approvedAt)}</div>
                            )}
                            {user.promotedAt && (
                              <div>Promoted: {formatDate(user.promotedAt)}</div>
                            )}
                            {user.rejectedAt && (
                              <div>Rejected: {formatDate(user.rejectedAt)}</div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex gap-2">
                        {canPromoteToAdmin(user) && (
                          <button
                            onClick={() => handlePromoteToAdmin(user.userId)}
                            disabled={processingUserId === user.userId || loading}
                            className="flex items-center gap-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
                          >
                            {processingUserId === user.userId ? (
                              <Loader2 className="animate-spin" size={16} />
                            ) : (
                              <UserCog size={16} />
                            )}
                            Promote to Admin
                          </button>
                        )}

                        {user.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(user.userId)}
                              disabled={processingUserId === user.userId || loading}
                              className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
                            >
                              {processingUserId === user.userId ? (
                                <Loader2 className="animate-spin" size={16} />
                              ) : (
                                <Check size={16} />
                              )}
                              Approve
                            </button>
                            <button
                              onClick={() => handleReject(user.userId)}
                              disabled={processingUserId === user.userId || loading}
                              className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
                            >
                              {processingUserId === user.userId ? (
                                <Loader2 className="animate-spin" size={16} />
                              ) : (
                                <X size={16} />
                              )}
                              Reject
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-xl">
        <h4 className="font-semibold text-blue-900 mb-2">Admin Actions</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Approved users will receive instant access to the app</li>
          <li>• Rejected users will see an access denied message</li>
          <li>• Promoted users will receive full admin privileges</li>
          <li>• Users must sign out and sign back in after approval or promotion to see changes</li>
        </ul>
      </div>
    </div>
  );
}
